import { useState, useMemo, useEffect, FocusEvent } from 'react';
import { Download, FileText, FileSpreadsheet, Search } from 'lucide-react';
import cx from 'classnames';
import DateNavigator from './DateNavigator';
import { exportToExcel, exportToPDF } from '../utils/reportExport';
import { useTestingApi } from '../hooks/useTestingApi';
import { useAthleteTestingViewData } from '../hooks/useAthleteTestingViewData';
import { ITestDefinition } from '../../models/testDefinition';
import { IEnabledTest } from '../../models/teamTestConfig';
import { resolveUnitLabel, findEnabledTest } from '../utils/resolveUnitLabel';
import {
  TestEntryRow,
  TestEntryRowProps,
  AnalyticsModeCards,
} from './AthleteTestingViewTableSubs';
import styles from './AthleteTestingViewTable.module.scss';

type CategoryKey = 'forza' | 'salto' | 'velocita' | 'resistenza' | 'mobilita' | 'composizione' | 'rtp_rts';
type TimeframeKey = '1m' | '3m' | '6m' | 'season' | 'all';

const categoryLabels: Record<CategoryKey, string> = {
  forza: 'Forza',
  salto: 'Salto',
  velocita: 'Velocità',
  resistenza: 'Resistenza',
  mobilita: 'Mobilità',
  composizione: 'Composizione',
  rtp_rts: 'RTP/RTS',
};

interface AthleteTestingViewTableProps {
  athleteId: string;
  selectedDate: string;
  onDateChange: (date: string) => void;
  mode: 'entry' | 'analytics';
  setMode: (mode: 'entry' | 'analytics') => void;
  hiddenTestIds?: Set<string>;
}

export default function AthleteTestingViewTable({
  athleteId,
  selectedDate,
  onDateChange,
  mode,
  hiddenTestIds,
}: AthleteTestingViewTableProps) {
  const { catalog, teamId, teamConfig } = useTestingApi();
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey>('forza');
  const [chartTimeframes, setChartTimeframes] = useState<Record<string, TimeframeKey>>({});
  const [isReportDropdownOpen, setIsReportDropdownOpen] = useState(false);
  const [testSearch, setTestSearch] = useState('');

  // Test della categoria selezionata (sez.7)
  const categoryTests = useMemo(
    () => catalog.filter((t: ITestDefinition) => t.category === selectedCategory),
    [catalog, selectedCategory]
  );

  // ── Hook dati backend: allineamento matrice↔profilo (sez.7) ──
  const {
    doneTestIds,
    getResult,
    getAllResults,
    getTeamValues,
    getLocalScalar,
    setLocalScalar,
    getLocalMetric,
    setLocalMetric,
    savingCells,
    resetLocalForDate,
    handleEditScalar,
    handleEditMultiMetric,
  } = useAthleteTestingViewData({ athleteId, selectedDate, categoryTests, teamId });

  // Reset valori locali al cambio data (sez.7)
  useEffect(() => {
    resetLocalForDate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Test visibili: solo quelli effettuati dall'atleta, meno quelli nascosti (sez.7.4)
  const visibleCategoryTests = useMemo(() => {
    let tests = categoryTests.filter((t: ITestDefinition) => doneTestIds.has(t.id));
    if (hiddenTestIds && hiddenTestIds.size > 0) {
      tests = tests.filter((t: ITestDefinition) => !hiddenTestIds.has(t.id));
    }
    return tests;
  }, [categoryTests, doneTestIds, hiddenTestIds]);

  /** Metriche output di un test: se >1 → multi-metrica impilata (sez.7.3) */
  const getMetrics = (test: ITestDefinition): string[] =>
    test.outputMetrics && test.outputMetrics.length > 1 ? test.outputMetrics : [];

  // ── Handler scalare: onBlur → salva al backend (sez.7) ──
  const handleScalarBlur = (test: ITestDefinition) => {
    const raw = getLocalScalar(test.id);
    if (!raw || raw.trim() === '') return;
    handleEditScalar(test, raw);
  };

  // ── Handler multi-metrica: onBlur (evita salvataggi intermedi durante focus interno) (sez.7.3) ──
  const handleMetricBlur = async (test: ITestDefinition, e?: FocusEvent<HTMLInputElement>) => {
    const nextCell = (e?.relatedTarget as HTMLElement | null)?.dataset?.cell;
    if (nextCell === test.id) return;

    const metrics = getMetrics(test);
    const values: Record<string, number> = {};
    let anyTouched = false;

    for (const m of metrics) {
      const raw = getLocalMetric(test.id, m);
      if (raw !== '') anyTouched = true;
      if (raw.trim() !== '') {
        const num = parseFloat(raw.replace(',', '.'));
        if (!isNaN(num)) values[m] = num;
      }
    }
    if (!anyTouched) return;
    await handleEditMultiMetric(test, values);
  };

  // ── Valore cella scalare: locale ha priorità sul persisted (sez.7) ──
  const getDisplayScalar = (test: ITestDefinition): string => {
    const local = getLocalScalar(test.id);
    if (local !== '') return local;
    const result = getResult(test.id);
    return result && result.value > 0 ? String(result.value) : '';
  };

  // ── Valore sotto-campo metrica: locale ha priorità sul persisted (sez.7.3) ──
  const getDisplayMetric = (test: ITestDefinition, metric: string): string => {
    const local = getLocalMetric(test.id, metric);
    if (local !== '') return local;
    const result = getResult(test.id);
    const v = result?.values?.[metric];
    return v != null ? String(v) : '';
  };

  // enabledTests dalla config team: necessario per resolveUnitLabel in ogni riga
  const enabledTests: IEnabledTest[] | undefined = (teamConfig as any)?.enabledTests;

  // Props condivise per ogni TestEntryRow (sez.7 / sez.7.3)
  // isSaving è calcolato per-test in EntryModeTable — qui passiamo savingCells come mappa
  const rowBaseProps = {
    selectedDate,
    getResult,
    getAllResults,
    getTeamValues,
    getMetrics,
    getDisplayScalar,
    getDisplayMetric,
    setLocalScalar,
    setLocalMetric,
    handleScalarBlur,
    handleMetricBlur,
    /** Risolve la label personalizzata per un'unità del test; fallback all'unità grezza */
    getUnitLabel: (test: ITestDefinition, unit: string) =>
      resolveUnitLabel(unit, findEnabledTest(test.id, enabledTests)),
  };

  return (
    <div className={styles.root}>
      {/* Category Pills + DateNavigator / Download */}
      <div className={styles.topBar}>
        <div className={styles.pillsWrapper}>
          <div className={styles.pills}>
            {(Object.keys(categoryLabels) as CategoryKey[]).map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={cx(styles.pill, selectedCategory === category && styles.pillActive)}
              >
                <p className={cx(styles.pillText, selectedCategory === category && styles.pillTextActive)}>
                  {categoryLabels[category]}
                </p>
              </button>
            ))}
          </div>
        </div>

        {mode === 'entry' ? (
          <DateNavigator selectedDate={selectedDate} onDateChange={onDateChange} />
        ) : (
          <div className={styles.downloadWrapper}>
            <button
              onClick={() => setIsReportDropdownOpen(!isReportDropdownOpen)}
              className={styles.downloadBtn}
            >
              <Download className={styles.downloadIcon} />
              <span>Scarica report</span>
            </button>

            {isReportDropdownOpen && (
              <>
                <div className={styles.dropdownBackdrop} onClick={() => setIsReportDropdownOpen(false)} />
                <div className={styles.dropdown}>
                  <button
                    onClick={() => {
                      const athlete = { id: athleteId, name: '' };
                      exportToExcel({
                        athleteId: athlete.id,
                        athleteName: athlete.name,
                        category: selectedCategory,
                        categoryLabel: categoryLabels[selectedCategory],
                        testResults: [],
                        athletes: [athlete],
                      });
                      setIsReportDropdownOpen(false);
                    }}
                    className={styles.dropdownItem}
                  >
                    <FileSpreadsheet className={styles.dropdownIcon} />
                    <span className={styles.dropdownText}>Scarica .xls</span>
                  </button>
                  <button
                    onClick={() => {
                      const athlete = { id: athleteId, name: '' };
                      exportToPDF({
                        athleteId: athlete.id,
                        athleteName: athlete.name,
                        category: selectedCategory,
                        categoryLabel: categoryLabels[selectedCategory],
                        testResults: [],
                        athletes: [athlete],
                      });
                      setIsReportDropdownOpen(false);
                    }}
                    className={styles.dropdownItem}
                  >
                    <FileText className={styles.dropdownIcon} />
                    <span className={styles.dropdownText}>Scarica .pdf</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Rendering condizionale su mode */}
      {mode === 'entry' ? (
        <EntryModeTable
          visibleCategoryTests={visibleCategoryTests}
          testSearch={testSearch}
          setTestSearch={setTestSearch}
          savingCells={savingCells}
          rowBaseProps={rowBaseProps}
        />
      ) : (
        <AnalyticsModeCards
          visibleCategoryTests={visibleCategoryTests}
          getAllResults={getAllResults}
          chartTimeframes={chartTimeframes}
          setChartTimeframes={setChartTimeframes}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EntryModeTable — tabella in modalità inserimento (sez.7)
// ─────────────────────────────────────────────────────────────────────────────
interface EntryModeTableProps {
  visibleCategoryTests: ITestDefinition[];
  testSearch: string;
  setTestSearch: (v: string) => void;
  savingCells: Record<string, boolean>;
  rowBaseProps: Omit<TestEntryRowProps, 'test' | 'isSaving'>;
}

function EntryModeTable({
  visibleCategoryTests,
  testSearch,
  setTestSearch,
  savingCells,
  rowBaseProps,
}: EntryModeTableProps) {
  const filteredTests = useMemo(
    () =>
      visibleCategoryTests.filter((test) =>
        testSearch ? test.name.toLowerCase().includes(testSearch.toLowerCase()) : true
      ),
    [visibleCategoryTests, testSearch]
  );

  return (
    <div className={styles.tableCard}>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            <th className={styles.thSearch}>
              <div className={styles.searchWrapper}>
                <Search className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Cerca test..."
                  value={testSearch}
                  onChange={(e) => setTestSearch(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
            </th>
            <th className={styles.thCenter}><p className={styles.thLabel}>VALORE</p></th>
            <th className={styles.thCenter}><p className={styles.thLabel}>ULTIMA</p></th>
            <th className={styles.thCenter}><p className={styles.thLabel}>MEDIA TEAM</p></th>
            <th className={styles.thCenter}><p className={styles.thLabel}>PERCENTILE</p></th>
            <th className={styles.thCenter}><p className={styles.thLabel}>Δ PREC.</p></th>
            <th className={styles.thCenter}><p className={styles.thLabel}>Δ BASELINE</p></th>
            <th className={styles.thCenter}><p className={styles.thLabel}>TREND</p></th>
          </tr>
        </thead>
        <tbody>
          {filteredTests.map((test) => (
            <TestEntryRow
              key={test.id}
              test={test}
              isSaving={!!savingCells[test.id]}
              {...rowBaseProps}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
